import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import React, { useMemo, useState } from 'react';

import type { SciImportOutcome, SciReport, SciReportRow } from '../../../@types/SciContactImport';
import { formatOfficer, SCI_OUTCOME_LABELS } from '../../../@types/SciContactImport';
import { downloadSciRefusalExcel, sciRowsToComplete } from '../../../utils/sciContactImportExport';
import Badge from '../../common/Badge';
import { showToast } from '../../common/Notification';

interface SciImportResultProps {
  report: SciReport;
  companyName: string;
}

const OUTCOME_BADGE: Record<SciImportOutcome, 'success' | 'info' | 'warning' | 'error'> = {
  imported: 'success',
  identical: 'info',
  conflict_kept: 'warning',
  refused: 'warning',
  not_importable: 'error',
};

// The row only carried the "Client en gestion" answer (no name nor email) and that answer was taken
const isManagedOnly = (row: SciReportRow): boolean => row.status === 'no_signal' && !!row.managedOutcome;

type ResultFilter = SciImportOutcome | 'all' | 'managed';

const SciImportResult: React.FC<SciImportResultProps> = ({ report, companyName }) => {
  const [outcomeFilter, setOutcomeFilter] = useState<ResultFilter>('all');
  const [exporting, setExporting] = useState(false);

  const notWrittenCount = sciRowsToComplete(report).length;
  const rows = useMemo(
    () => report.rows.filter((row) => {
      if (outcomeFilter === 'all') {return true;}
      if (outcomeFilter === 'managed') {return row.managedOutcome === 'updated';}
      return row.outcome === outcomeFilter && !isManagedOnly(row);
    }),
    [report.rows, outcomeFilter],
  );

  const handleExport = async() => {
    setExporting(true);
    try {
      await downloadSciRefusalExcel(report, companyName);
    } catch (error) {
      console.error('Error exporting SCI refusals:', error);
      showToast('error', 'Erreur lors de la génération de l\'Excel.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
        {(Object.keys(SCI_OUTCOME_LABELS) as SciImportOutcome[]).map((outcome) => (
          <div key={outcome} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <p className="text-xs text-gray-500">{SCI_OUTCOME_LABELS[outcome]}</p>
            <p className="text-2xl font-bold text-gray-800">
              {(report.summary[outcome] ?? 0) - (outcome === 'not_importable' ? report.rows.filter(isManagedOnly).length : 0)}
            </p>
          </div>
        ))}
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="text-xs text-gray-500">dont choix manuels</p>
          <p className="text-2xl font-bold text-gray-800">{report.summary.manual ?? 0}</p>
        </div>
      </div>
      {(report.summary.managed ?? 0) > 0 && (
        <p className="text-sm text-gray-700">
          {`« Client en gestion » mis à jour sur ${report.summary.managed} SCI.`}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <select
          value={outcomeFilter}
          onChange={(event) => setOutcomeFilter(event.target.value as ResultFilter)}
          className="rounded-md border border-gray-300 px-2 py-1 text-sm"
        >
          <option value="all">Toutes les lignes</option>
          {(Object.keys(SCI_OUTCOME_LABELS) as SciImportOutcome[]).map((outcome) => (
            <option key={outcome} value={outcome}>{SCI_OUTCOME_LABELS[outcome]}</option>
          ))}
          {(report.summary.managed ?? 0) > 0 && <option value="managed">Client en gestion mis à jour</option>}
        </select>
        <button
          type="button"
          onClick={handleExport}
          disabled={exporting || notWrittenCount === 0}
          className="ml-auto flex items-center gap-2 rounded-md bg-rgpd-blue-dark px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          {`Excel des SCI non importées (${notWrittenCount})`}
        </button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase">
            <tr>
              <th className="px-3 py-2">SCI</th>
              <th className="px-3 py-2">Résultat</th>
              <th className="px-3 py-2">Contact</th>
              <th className="px-3 py-2">Dirigeant</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row) => (
              <tr key={`${row.line}-${row.siren}`}>
                <td className="px-3 py-2 align-top">
                  <p className="font-medium text-gray-800">{row.denomination || '—'}</p>
                  <p className="font-mono text-xs text-gray-500">{row.siren}</p>
                </td>
                <td className="px-3 py-2 align-top">
                  {row.status === 'no_signal' && row.managedOutcome ? (
                    // The row only carried the "Client en gestion" answer: nothing was expected for the contact
                    <Badge size="small" mode="info" text="Client en gestion seul" />
                  ) : (
                    <>
                      <Badge size="small" mode={OUTCOME_BADGE[row.outcome]} text={SCI_OUTCOME_LABELS[row.outcome]} />
                      {row.motif && <p className="mt-1 text-xs text-gray-500">{row.motif}</p>}
                    </>
                  )}
                  {row.managedOutcome === 'updated' && (
                    <p className="mt-1 text-xs text-emerald-700">{`Client en gestion : ${row.managed ? 'Oui' : 'Non'}`}</p>
                  )}
                  {row.managedOutcome === 'unchanged' && (
                    <p className="mt-1 text-xs text-gray-500">{`Client en gestion déjà à « ${row.managed ? 'Oui' : 'Non'} »`}</p>
                  )}
                  {row.managed !== null && row.managed !== undefined && !row.managedOutcome && (
                    <p className="mt-1 text-xs text-gray-500">Client en gestion ignoré (ligne non importable)</p>
                  )}
                </td>
                <td className="px-3 py-2 align-top text-xs text-gray-700">
                  {row.contact ? (
                    <>
                      <p>{[row.contact.civilite, row.contact.nom, row.contact.prenom].filter(Boolean).join(' ')}</p>
                      <p className="break-all">{[row.contact.email, row.contact.telephone].filter(Boolean).join(' · ')}</p>
                    </>
                  ) : '—'}
                </td>
                <td className="px-3 py-2 align-top text-xs text-gray-700">
                  {row.officer ? `${formatOfficer(row.officer)} (${row.officer.role})` : '—'}
                  {row.manual && <p className="font-medium text-orange-700">Choix manuel</p>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SciImportResult;
